import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft } from "lucide-react";

import { ContentArchiveMarkdown } from "@/components/content-archive-markdown";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { getContentArchiveEntry } from "@/data/contentArchive";
import { breadcrumbJsonLdScript, contentBreadcrumbTrail } from "@/lib/breadcrumbs";
import { pageSeo } from "@/lib/seo";

const NOINDEX_META = { name: "robots", content: "noindex, nofollow" } as const;

export const Route = createFileRoute("/content/$slug")({
  loader: ({ params }) => getContentArchiveEntry(params.slug) ?? null,
  head: ({ params }) => {
    const entry = getContentArchiveEntry(params?.slug ?? "");
    const path = `/content/${params?.slug ?? ""}`;
    return {
      ...pageSeo({
        path,
        title: entry ? `${entry.title} | Content Archive` : "Entry Not Found | Content Archive",
        description: "Internal CRS Energy content archive entry for authorised team members only. Not intended for public use or search indexing.",
      }),
      meta: [NOINDEX_META],
      scripts: entry
        ? [breadcrumbJsonLdScript(contentBreadcrumbTrail(entry.title, path))]
        : undefined,
    };
  },
  component: ContentArchiveDetailPage,
});

function ContentArchiveDetailPage() {
  const entry = Route.useLoaderData();

  if (!entry) {
    return (
      <section className="container-page max-w-3xl py-16 md:py-20">
        <Link
          to="/content"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to archive
        </Link>
        <h1 className="text-2xl font-bold text-foreground mb-2">Entry not found</h1>
        <p className="text-sm text-muted-foreground">
          No archive entry matches this URL.{" "}
          <Link to="/content" className="text-accent hover:underline">
            Return to the archive index
          </Link>
          .
        </p>
      </section>
    );
  }

  return (
    <section className="container-page max-w-3xl py-16 md:py-20">
      <PageBreadcrumb items={contentBreadcrumbTrail(entry.title, `/content/${entry.slug}`)} />

      <header className="mb-8 mt-4 space-y-3 border-b border-border pb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">{entry.title}</h1>
        <dl className="grid gap-2 text-sm">
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">Source page:</dt>
            <dd className="font-medium text-foreground">{entry.sourcePath}</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">Date archived:</dt>
            <dd className="font-medium text-foreground">
              {format(parseISO(entry.dateArchived), "d MMMM yyyy")}
            </dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">Reason:</dt>
            <dd className="text-foreground">{entry.reason}</dd>
          </div>
        </dl>
      </header>

      <ContentArchiveMarkdown content={entry.content} />
    </section>
  );
}
